<template>
    <v-container class="py-12">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Despesas</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Home', to: '/home' },
                            { text: 'Financeiro', to: '/financeiro', exact: true },
                            { text: 'Despesas', disabled: true }
                        ]" />
                    </div>
                    <div class="button-group">
                        <v-btn v-if="canManage" outlined color="primary" elevation="0" @click="categoriesDialog = true">
                            <v-icon left small>mdi-shape-outline</v-icon>Categorias
                        </v-btn>
                        <v-btn outlined color="primary" elevation="0" :loading="exporting" @click="exportList">
                            <v-icon left small>mdi-file-excel-outline</v-icon>Exportar
                        </v-btn>
                        <v-btn v-if="canManage" color="primary" elevation="0" @click="openForm()">
                            <v-icon left small>mdi-plus</v-icon>Nova despesa
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-card>
            <v-card-text>
                <v-row dense align="center">
                    <v-col cols="12" sm="6" md="2">
                        <v-text-field dense outlined hide-details type="date" label="Vencimento de" v-model="filter.start" />
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                        <v-text-field dense outlined hide-details type="date" label="Até" v-model="filter.end" />
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                        <v-text-field dense outlined hide-details clearable label="Descrição, fornecedor ou documento"
                            prepend-inner-icon="mdi-magnify" v-model="filter.search" @keyup.enter="load(1)" />
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                        <v-autocomplete dense outlined hide-details clearable label="Categoria" :items="activeCategories"
                            item-text="name" item-value="id" v-model="filter.category_id" @change="load(1)" />
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center flex-wrap" style="gap: 4px">
                        <v-btn color="primary" elevation="0" :loading="loading" @click="load(1)">Filtrar</v-btn>
                        <v-btn small text color="primary" @click="setMonth(-1)">Mês passado</v-btn>
                        <v-btn small text color="primary" @click="setMonth(0)">Este mês</v-btn>
                        <v-btn small text color="primary" @click="setMonth(1)">Próximo mês</v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-alert v-if="summary.overdue.count" dense text type="error" class="body-2 mb-0">
            <div class="d-flex align-center flex-wrap" style="gap: 8px">
                <span><strong>{{ summary.overdue.count }}</strong> despesa(s) vencida(s) somando
                    <strong>{{ money(summary.overdue.total) }}</strong>.</span>
                <v-spacer />
                <v-btn x-small text color="error" @click="setStatus('overdue')">Ver vencidas</v-btn>
            </div>
        </v-alert>

        <div class="stat-grid">
            <stat-card label="A pagar no período" icon="mdi-calendar-clock" :value="money(summary.open.total)"
                :hint="`${summary.open.count} lançamento(s) em aberto`" color="#1450b5" clickable @click="setStatus('open')" />
            <stat-card label="Vencidas" icon="mdi-alert-circle-outline" :value="money(summary.overdue.total)"
                :hint="`${summary.overdue.count} em atraso (qualquer data)`" :color="summary.overdue.count ? '#d32f2f' : ''"
                :alert="!!summary.overdue.count" clickable @click="setStatus('overdue')" />
            <stat-card label="Pagas no período" icon="mdi-check-circle-outline" :value="money(summary.paid.total)"
                :hint="`${summary.paid.count} pagamento(s)`" color="#2e7d32" clickable @click="setStatus('paid')" />
            <stat-card label="Total do período" icon="mdi-sigma" :value="money(summary.period.total)"
                :hint="`vencem nos próximos 7 dias: ${money(summary.next_7_days.total)}`" clickable @click="setStatus('all')" />
        </div>

        <v-card v-if="summary.by_category.length" class="section-card">
            <v-card-title><div>Por categoria<small>Despesas do período pelo vencimento</small></div></v-card-title>
            <v-card-text>
                <div v-for="item in summary.by_category" :key="String(item.category_id)" class="category-bar">
                    <span class="category-bar__name">{{ item.name }}</span>
                    <div class="category-bar__track">
                        <div :style="{ width: barWidth(item.total) + '%', background: groupColor(item.group) }" />
                    </div>
                    <strong>{{ money(item.total) }}</strong>
                    <em>{{ percent(item.total) }}</em>
                </div>
            </v-card-text>
        </v-card>

        <v-card>
            <v-tabs v-model="tab">
                <v-tab>Lançamentos</v-tab>
                <v-tab>Recorrentes ({{ recurrences.filter(item => item.active).length }})</v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">
                <!-- LANÇAMENTOS -->
                <v-tab-item>
                    <div class="px-4 pt-3">
                        <v-chip-group v-model="filter.status" mandatory active-class="primary--text" @change="load(1)">
                            <v-chip v-for="option in statusOptions" :key="option.value" :value="option.value" small outlined filter>
                                {{ option.text }}
                            </v-chip>
                        </v-chip-group>
                    </div>

                    <v-data-table :headers="headers" :items="list.data" :loading="loading" :server-items-length="list.total"
                        :options.sync="tableOptions" :footer-props="{ itemsPerPageOptions: [20, 50, 100] }"
                        class="app-table" no-data-text="Nenhuma despesa no período" loading-text="Carregando...">
                        <template v-slot:[`item.due_date`]="{ item }">
                            <span :class="{ 'red--text font-weight-bold': item.overdue }">{{ date(item.due_date) }}</span>
                            <div v-if="item.status === 'paid'" class="caption grey--text">pago {{ date(item.paid_at) }}</div>
                        </template>
                        <template v-slot:[`item.description`]="{ item }">
                            <div class="font-weight-medium">{{ item.description }}</div>
                            <div class="caption grey--text">
                                {{ item.supplier || 'Sem fornecedor' }}
                                <template v-if="item.document_number"> · doc. {{ item.document_number }}</template>
                                <v-chip v-if="item.recurrence_id" x-small outlined class="ml-1">
                                    <v-icon x-small left>mdi-autorenew</v-icon>recorrente
                                </v-chip>
                            </div>
                        </template>
                        <template v-slot:[`item.category_name`]="{ item }">
                            <span class="text-no-wrap">{{ item.category_name || '-' }}</span>
                            <div class="caption grey--text">{{ competence(item.competence_month) }}</div>
                        </template>
                        <template v-slot:[`item.vehicle_code`]="{ item }">
                            <span v-if="item.vehicle_code" class="text-no-wrap">{{ item.vehicle_code }} · {{ item.vehicle_plate }}</span>
                            <span v-else class="grey--text">-</span>
                        </template>
                        <template v-slot:[`item.amount`]="{ item }">
                            <strong class="text-no-wrap">{{ money(item.amount) }}</strong>
                            <div v-if="item.status === 'paid' && Number(item.paid_amount) !== Number(item.amount)" class="caption grey--text text-no-wrap">
                                pago {{ money(item.paid_amount) }}
                            </div>
                        </template>
                        <template v-slot:[`item.status`]="{ item }">
                            <v-chip x-small dark :color="statusInfo(item).color">{{ statusInfo(item).text }}</v-chip>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                            <div class="d-flex align-center justify-end text-no-wrap">
                                <v-btn v-if="item.status === 'open' && canPay" x-small color="success" elevation="0" class="mr-1"
                                    @click="openPay(item)">Pagar</v-btn>
                                <v-btn v-if="item.receipt_resource_id || item.attachment_resource_id" icon x-small title="Anexos"
                                    @click="openResource(item.receipt_resource_id || item.attachment_resource_id)">
                                    <v-icon small>mdi-paperclip</v-icon>
                                </v-btn>
                                <v-menu offset-y left>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon x-small v-bind="attrs" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item v-if="canManage && item.status !== 'paid'" @click="openForm(item)">
                                            <v-list-item-title>Editar</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item v-if="canPay && item.status === 'paid'" @click="unpay(item)">
                                            <v-list-item-title>Estornar pagamento</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item v-if="canManage && item.status !== 'paid'" @click="toggleCancel(item)">
                                            <v-list-item-title>{{ item.status === 'cancelled' ? 'Reativar' : 'Cancelar' }}</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item v-if="canManage && item.status !== 'paid'" @click="remove(item)">
                                            <v-list-item-title class="red--text">Excluir</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>
                        </template>
                    </v-data-table>
                </v-tab-item>

                <!-- RECORRENTES -->
                <v-tab-item>
                    <p class="body-2 grey--text px-4 pt-4 mb-0">
                        Despesas fixas (aluguel, contador, internet...). O lançamento de cada mês é criado sozinho, até o mês
                        seguinte ao atual. Alterar uma recorrente atualiza os lançamentos futuros ainda em aberto.
                    </p>
                    <v-data-table :headers="recurrenceHeaders" :items="recurrences" :items-per-page="50" class="app-table"
                        no-data-text="Nenhuma despesa recorrente. Use &quot;Nova despesa&quot; e marque &quot;Recorrente&quot;.">
                        <template v-slot:[`item.description`]="{ item }">
                            <div class="font-weight-medium">{{ item.description }}</div>
                            <div class="caption grey--text">{{ item.supplier_name || '' }}</div>
                        </template>
                        <template v-slot:[`item.amount`]="{ item }"><strong>{{ money(item.amount) }}</strong></template>
                        <template v-slot:[`item.due_day`]="{ item }">Dia {{ item.due_day }}</template>
                        <template v-slot:[`item.start_month`]="{ item }">
                            {{ competence(item.start_month) }} até {{ item.end_month ? competence(item.end_month) : 'sem fim' }}
                        </template>
                        <template v-slot:[`item.active`]="{ item }">
                            <v-chip x-small dark :color="item.active ? 'green' : 'grey'">{{ item.active ? 'Ativa' : 'Encerrada' }}</v-chip>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                            <div v-if="canManage" class="d-flex justify-end">
                                <v-btn icon x-small @click="openForm(item, true)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                                <v-btn v-if="item.active" icon x-small color="error" title="Encerrar" @click="endRecurrence(item)">
                                    <v-icon small>mdi-stop-circle-outline</v-icon>
                                </v-btn>
                            </div>
                        </template>
                    </v-data-table>
                </v-tab-item>
            </v-tabs-items>
        </v-card>

        <!-- DESPESA / RECORRENTE -->
        <v-dialog v-model="form.open" max-width="680px" persistent>
            <v-card>
                <v-card-title>{{ formTitle }}</v-card-title>
                <v-card-text>
                    <v-form ref="form">
                        <v-row dense>
                            <v-col cols="12" v-if="!form.id && !form.isRecurrence">
                                <v-btn-toggle v-model="form.mode" mandatory dense color="primary" class="mb-2">
                                    <v-btn small value="single">Única</v-btn>
                                    <v-btn small value="installments">Parcelada</v-btn>
                                    <v-btn small value="recurring">Recorrente mensal</v-btn>
                                </v-btn-toggle>
                            </v-col>
                            <v-col cols="12" sm="8">
                                <v-text-field dense outlined label="Descrição" v-model="form.description"
                                    :rules="[v => !!(v && v.trim()) || 'Informe a descrição']" />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-currency-field dense outlined prefix="R$" :label="amountLabel" v-model="form.amount"
                                    :rules="[v => Number(v) > 0 || 'Informe o valor']" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-autocomplete dense outlined label="Categoria" :items="activeCategories" item-text="name"
                                    item-value="id" v-model="form.category_id" clearable />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-combobox dense outlined label="Fornecedor" :items="supplierNames" v-model="form.supplier_name"
                                    clearable hint="Escolha um fornecedor cadastrado ou digite o nome" />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field dense outlined type="date" :label="form.mode === 'recurring' || form.isRecurrence ? 'Primeiro vencimento' : 'Vencimento'"
                                    v-model="form.due_date" :rules="[v => !!v || 'Informe o vencimento']" />
                            </v-col>
                            <v-col cols="12" sm="4" v-if="form.mode === 'installments'">
                                <v-text-field dense outlined type="number" min="2" max="120" label="Nº de parcelas" v-model="form.installments"
                                    :rules="[v => Number(v) >= 2 || 'Mínimo 2']" :hint="installmentHint" persistent-hint />
                            </v-col>
                            <v-col cols="12" sm="4" v-if="form.mode === 'recurring' || form.isRecurrence">
                                <v-text-field dense outlined type="month" label="Até (vazio = sem fim)" v-model="form.end_month" />
                            </v-col>
                            <v-col cols="12" sm="4" v-if="form.mode === 'single'">
                                <v-text-field dense outlined type="month" label="Competência" v-model="form.competence_month"
                                    hint="Mês a que a despesa pertence" persistent-hint />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-autocomplete dense outlined clearable label="Veículo (opcional)" :items="vehicleOptions"
                                    item-text="text" item-value="id" v-model="form.vehicle_id"
                                    hint="Entra no custo do veículo como outras despesas" persistent-hint />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select dense outlined clearable label="Forma de pagamento" :items="options.payment_methods"
                                    v-model="form.payment_method" />
                            </v-col>
                            <template v-if="!form.isRecurrence">
                                <v-col cols="12" sm="6">
                                    <v-text-field dense outlined label="Nº do documento / nota" v-model="form.document_number" />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-file-input dense outlined prepend-icon="" prepend-inner-icon="mdi-paperclip"
                                        :label="form.attachment_resource_id ? 'Trocar anexo (boleto, nota)' : 'Anexo (boleto, nota)'"
                                        accept="image/*,application/pdf" v-model="form.file" />
                                </v-col>
                            </template>
                            <v-col cols="12">
                                <v-textarea dense outlined rows="2" label="Observação" v-model="form.notes" />
                            </v-col>
                            <template v-if="!form.id && form.mode === 'single' && canPay">
                                <v-col cols="12" sm="4">
                                    <v-checkbox dense hide-details class="mt-0" label="Já está paga" v-model="form.paid" />
                                </v-col>
                                <v-col cols="12" sm="4" v-if="form.paid">
                                    <v-text-field dense outlined hide-details type="date" label="Pago em" v-model="form.paid_at" />
                                </v-col>
                            </template>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="form.open = false">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" :loading="saving" @click="saveForm">Salvar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- PAGAMENTO -->
        <v-dialog v-model="pay.open" max-width="460px">
            <v-card>
                <v-card-title>Registrar pagamento</v-card-title>
                <v-card-text>
                    <p class="body-2 mb-4">{{ pay.item && pay.item.description }} · {{ money(pay.item && pay.item.amount) }}</p>
                    <v-text-field dense outlined type="date" label="Data do pagamento" v-model="pay.paid_at" />
                    <v-currency-field dense outlined prefix="R$" label="Valor pago" v-model="pay.paid_amount"
                        hint="Com juros, multa ou desconto, informe o valor efetivamente pago" persistent-hint class="mb-2" />
                    <v-select dense outlined clearable label="Forma de pagamento" :items="options.payment_methods" v-model="pay.payment_method" />
                    <v-file-input dense outlined prepend-icon="" prepend-inner-icon="mdi-paperclip" label="Comprovante (opcional)"
                        accept="image/*,application/pdf" v-model="pay.file" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="pay.open = false">Cancelar</v-btn>
                    <v-btn color="success" elevation="0" :loading="saving" @click="confirmPay">Confirmar pagamento</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- CATEGORIAS -->
        <v-dialog v-model="categoriesDialog" max-width="620px">
            <v-card>
                <v-card-title>Categorias de despesa</v-card-title>
                <v-card-text>
                    <v-simple-table dense class="mb-4">
                        <tbody>
                            <tr v-for="category in options.categories" :key="category.id">
                                <td>
                                    <v-text-field dense hide-details v-model="category.name" :disabled="!category.active"
                                        @change="saveCategory(category)" />
                                </td>
                                <td style="width: 170px">
                                    <v-select dense hide-details :items="groupOptions" v-model="category.group"
                                        :disabled="!category.active" @change="saveCategory(category)" />
                                </td>
                                <td class="text-right" style="width: 110px">
                                    <v-btn v-if="category.active" x-small text color="error" @click="removeCategory(category)">Desativar</v-btn>
                                    <v-btn v-else x-small text color="primary" @click="saveCategory({ ...category, active: true })">Reativar</v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <div class="d-flex" style="gap: 8px">
                        <v-text-field dense outlined hide-details label="Nova categoria" v-model="newCategory.name" />
                        <v-select dense outlined hide-details label="Grupo" :items="groupOptions" v-model="newCategory.group" style="max-width: 180px" />
                        <v-btn color="primary" elevation="0" :disabled="!newCategory.name" @click="addCategory">Adicionar</v-btn>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="categoriesDialog = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

const GROUP_COLORS = {
    operational: '#1450b5', administrative: '#5b6fd6', personnel: '#2bb3a3',
    taxes: '#d97706', vehicles: '#d9534f', other: '#8a96ab'
}

const emptySummary = () => ({
    open: { total: 0, count: 0 }, overdue: { total: 0, count: 0 }, paid: { total: 0, count: 0 },
    period: { total: 0, count: 0 }, next_7_days: { total: 0, count: 0 }, by_category: []
})

const emptyForm = () => ({
    open: false, id: null, isRecurrence: false, mode: 'single', description: '', amount: null, category_id: null,
    supplier_name: null, due_date: moment().format('YYYY-MM-DD'), competence_month: moment().format('YYYY-MM'),
    installments: 2, end_month: null, vehicle_id: null, payment_method: null, document_number: '', notes: '',
    attachment_resource_id: null, file: null, paid: false, paid_at: moment().format('YYYY-MM-DD')
})

export default {
    name: 'ExpenseList',

    data: () => ({
        loading: false,
        saving: false,
        exporting: false,
        tab: 0,
        options: { categories: [], suppliers: [], vehicles: [], payment_methods: [], groups: {} },
        filter: {
            start: moment().startOf('month').format('YYYY-MM-DD'),
            end: moment().endOf('month').format('YYYY-MM-DD'),
            search: '',
            category_id: null,
            status: 'all'
        },
        list: { data: [], total: 0 },
        summary: emptySummary(),
        recurrences: [],
        tableOptions: { page: 1, itemsPerPage: 20 },
        form: emptyForm(),
        pay: { open: false, item: null, paid_at: null, paid_amount: null, payment_method: null, file: null },
        categoriesDialog: false,
        newCategory: { name: '', group: 'administrative' },
        statusOptions: [
            { value: 'all', text: 'Todas' },
            { value: 'open', text: 'A pagar' },
            { value: 'overdue', text: 'Vencidas' },
            { value: 'paid', text: 'Pagas no período' },
            { value: 'cancelled', text: 'Canceladas' }
        ],
        headers: [
            { text: 'Vencimento', value: 'due_date', sortable: false, width: 120 },
            { text: 'Descrição', value: 'description', sortable: false },
            { text: 'Categoria', value: 'category_name', sortable: false },
            { text: 'Veículo', value: 'vehicle_code', sortable: false },
            { text: 'Valor', value: 'amount', sortable: false, align: 'end' },
            { text: 'Situação', value: 'status', sortable: false },
            { text: '', value: 'actions', sortable: false, align: 'end', width: 150 }
        ],
        recurrenceHeaders: [
            { text: 'Descrição', value: 'description' },
            { text: 'Categoria', value: 'category_name' },
            { text: 'Valor', value: 'amount', align: 'end' },
            { text: 'Vencimento', value: 'due_day' },
            { text: 'Vigência', value: 'start_month' },
            { text: 'Situação', value: 'active' },
            { text: '', value: 'actions', sortable: false, align: 'end', width: 90 }
        ]
    }),

    computed: {
        canManage() {
            return this.$check(['financial.expenses.manage'])
        },
        canPay() {
            return this.$check(['financial.expenses.pay'])
        },
        activeCategories() {
            return this.options.categories.filter(item => item.active)
        },
        supplierNames() {
            return this.options.suppliers.map(item => item.name)
        },
        vehicleOptions() {
            return this.options.vehicles.map(item => ({ id: item.id, text: `${item.code} · ${item.plate || ''}` }))
        },
        groupOptions() {
            return Object.keys(this.options.groups).map(value => ({ value, text: this.options.groups[value] }))
        },
        formTitle() {
            if (this.form.isRecurrence) return 'Editar despesa recorrente'
            return this.form.id ? 'Editar despesa' : 'Nova despesa'
        },
        amountLabel() {
            if (this.form.mode === 'installments') return 'Valor total'
            if (this.form.mode === 'recurring' || this.form.isRecurrence) return 'Valor mensal'
            return 'Valor'
        },
        installmentHint() {
            const count = Number(this.form.installments) || 0
            return count >= 2 && Number(this.form.amount) > 0 ? `${count}x de ${this.money(Number(this.form.amount) / count)}` : ''
        }
    },

    watch: {
        tableOptions: {
            deep: true,
            handler(value, old) {
                if (old && (value.page !== old.page || value.itemsPerPage !== old.itemsPerPage)) this.load()
            }
        }
    },

    created() {
        this.loadOptions()
        this.load(1)
        this.loadRecurrences()
    },

    methods: {
        notifyError(error) {
            this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
        },

        async loadOptions() {
            try {
                const response = await axios.get('/api/v2/financial/expenses/options')
                this.options = response.data.content
            } catch (error) {
                this.notifyError(error)
            }
        },

        async load(page) {
            if (page) this.tableOptions.page = page
            this.loading = true
            try {
                const response = await axios.get('/api/v2/financial/expenses', {
                    params: {
                        start: this.filter.start,
                        end: this.filter.end,
                        search: this.filter.search || '',
                        category_id: this.filter.category_id || '',
                        status: this.filter.status,
                        page: this.tableOptions.page,
                        limit: this.tableOptions.itemsPerPage
                    }
                })
                const content = response.data.content
                this.list = { data: content.data, total: content.total }
                this.summary = content.summary || emptySummary()
            } catch (error) {
                this.notifyError(error)
            } finally {
                this.loading = false
            }
        },

        async loadRecurrences() {
            try {
                const response = await axios.get('/api/v2/financial/expense-recurrences')
                this.recurrences = response.data.content
            } catch (error) {
                this.notifyError(error)
            }
        },

        reloadAll() {
            this.load()
            this.loadRecurrences()
        },

        setMonth(offset) {
            const month = moment().add(offset, 'month')
            this.filter.start = month.clone().startOf('month').format('YYYY-MM-DD')
            this.filter.end = month.clone().endOf('month').format('YYYY-MM-DD')
            this.load(1)
        },

        setStatus(status) {
            this.tab = 0
            this.filter.status = status
            this.load(1)
        },

        statusInfo(item) {
            if (item.status === 'paid') return { text: 'Paga', color: 'green' }
            if (item.status === 'cancelled') return { text: 'Cancelada', color: 'grey' }
            if (item.overdue) return { text: 'Vencida', color: 'red' }
            return { text: 'A pagar', color: 'blue' }
        },

        groupColor(group) {
            return GROUP_COLORS[group] || GROUP_COLORS.other
        },

        barWidth(value) {
            const max = Math.max(...this.summary.by_category.map(item => Number(item.total) || 0), 1)
            return Math.max((Number(value) || 0) / max * 100, 2)
        },

        percent(value) {
            const total = Number(this.summary.period.total) || 0
            return total ? `${(Number(value) / total * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : ''
        },

        // ==== Formulário ====

        openForm(item, isRecurrence = false) {
            const base = emptyForm()

            if (item && isRecurrence) {
                this.form = {
                    ...base, open: true, id: item.id, isRecurrence: true, mode: 'recurring',
                    description: item.description, amount: Number(item.amount), category_id: item.category_id,
                    supplier_name: item.supplier_name, vehicle_id: item.vehicle_id, payment_method: item.payment_method,
                    notes: item.notes || '', end_month: item.end_month ? item.end_month.slice(0, 7) : null,
                    due_date: moment(item.start_month).date(Math.min(item.due_day, moment(item.start_month).daysInMonth())).format('YYYY-MM-DD')
                }
            } else if (item) {
                this.form = {
                    ...base, open: true, id: item.id, mode: 'single',
                    description: item.description, amount: Number(item.amount), category_id: item.category_id,
                    supplier_name: item.supplier, vehicle_id: item.vehicle_id, payment_method: item.payment_method,
                    document_number: item.document_number || '', notes: item.notes || '',
                    due_date: item.due_date, competence_month: (item.competence_month || item.due_date).slice(0, 7),
                    attachment_resource_id: item.attachment_resource_id
                }
            } else {
                this.form = { ...base, open: true }
            }

            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
        },

        async upload(file) {
            if (!file) return null
            const formData = new FormData()
            formData.append('file', file)
            const response = await axios.post('/api/v2/resources', formData)
            return response.data.content && response.data.content.id
        },

        supplierPayload(name) {
            const supplier = this.options.suppliers.find(item => item.name === name)
            return { supplier_id: supplier ? supplier.id : null, supplier_name: name || null }
        },

        async saveForm() {
            if (!this.$refs.form.validate()) return

            this.saving = true
            try {
                const form = this.form
                const attachment = form.file ? await this.upload(form.file) : form.attachment_resource_id
                const payload = {
                    description: form.description,
                    amount: form.amount,
                    category_id: form.category_id,
                    ...this.supplierPayload(form.supplier_name),
                    vehicle_id: form.vehicle_id,
                    payment_method: form.payment_method,
                    document_number: form.document_number,
                    attachment_resource_id: attachment,
                    notes: form.notes,
                    due_date: form.due_date,
                    competence_month: form.mode === 'single' && form.competence_month ? `${form.competence_month}-01` : null
                }

                if (form.isRecurrence) {
                    await axios.put(`/api/v2/financial/expense-recurrences/${form.id}`, {
                        ...payload,
                        start_month: form.due_date,
                        due_day: moment(form.due_date).date(),
                        end_month: form.end_month ? `${form.end_month}-01` : null
                    })
                } else if (form.id) {
                    await axios.put(`/api/v2/financial/expenses/${form.id}`, payload)
                } else {
                    await axios.post('/api/v2/financial/expenses', {
                        ...payload,
                        installments: form.mode === 'installments' ? form.installments : 1,
                        recurring: form.mode === 'recurring',
                        end_month: form.mode === 'recurring' && form.end_month ? `${form.end_month}-01` : null,
                        paid: form.mode === 'single' && form.paid,
                        paid_at: form.paid_at
                    })
                }

                this.form.open = false
                this.$dialog.notify.success('Despesa salva', { position: 'top-right', timeout: 3000 })
                this.reloadAll()
            } catch (error) {
                this.notifyError(error)
            } finally {
                this.saving = false
            }
        },

        // ==== Ações ====

        openPay(item) {
            this.pay = {
                open: true, item, paid_at: moment().format('YYYY-MM-DD'), paid_amount: Number(item.amount),
                payment_method: item.payment_method, file: null
            }
        },

        async confirmPay() {
            this.saving = true
            try {
                const receipt = await this.upload(this.pay.file)
                await axios.post(`/api/v2/financial/expenses/${this.pay.item.id}/pay`, {
                    paid_at: this.pay.paid_at,
                    paid_amount: this.pay.paid_amount,
                    payment_method: this.pay.payment_method,
                    receipt_resource_id: receipt
                })
                this.pay.open = false
                this.$dialog.notify.success('Pagamento registrado', { position: 'top-right', timeout: 3000 })
                this.load()
            } catch (error) {
                this.notifyError(error)
            } finally {
                this.saving = false
            }
        },

        async confirmAction(title, text) {
            return this.$dialog.confirm({ title, text, actions: { false: 'Voltar', true: 'Confirmar' } })
        },

        async unpay(item) {
            if (!(await this.confirmAction('Estornar pagamento', `Estornar o pagamento de "${item.description}"? Ela volta a ficar a pagar.`))) return
            try {
                await axios.post(`/api/v2/financial/expenses/${item.id}/unpay`)
                this.load()
            } catch (error) {
                this.notifyError(error)
            }
        },

        async toggleCancel(item) {
            const cancelling = item.status !== 'cancelled'
            if (cancelling && !(await this.confirmAction('Cancelar despesa', `Cancelar "${item.description}"? Ela deixa de contar nos totais.`))) return
            try {
                await axios.post(`/api/v2/financial/expenses/${item.id}/cancel`)
                this.load()
            } catch (error) {
                this.notifyError(error)
            }
        },

        async remove(item) {
            const extra = item.recurrence_id ? ' Por ser recorrente, este mês não será gerado de novo.' : ''
            if (!(await this.confirmAction('Excluir despesa', `Excluir "${item.description}"?${extra}`))) return
            try {
                await axios.delete(`/api/v2/financial/expenses/${item.id}`)
                this.load()
            } catch (error) {
                this.notifyError(error)
            }
        },

        async endRecurrence(item) {
            if (!(await this.confirmAction('Encerrar recorrente', `Encerrar "${item.description}"? Os lançamentos futuros ainda em aberto serão removidos; os já pagos ficam.`))) return
            try {
                const response = await axios.delete(`/api/v2/financial/expense-recurrences/${item.id}`)
                this.recurrences = response.data.content
                this.load()
            } catch (error) {
                this.notifyError(error)
            }
        },

        async openResource(id) {
            try {
                const response = await axios.get(`/api/v2/resources/${id}`, { responseType: 'blob' })
                window.open(URL.createObjectURL(response.data), '_blank')
            } catch (error) {
                this.$dialog.notify.error('Não foi possível abrir o anexo', { position: 'top-right', timeout: 4000 })
            }
        },

        // ==== Categorias ====

        async saveCategory(category) {
            try {
                const response = await axios.put(`/api/v2/financial/expense-categories/${category.id}`, category)
                this.options.categories = response.data.content
            } catch (error) {
                this.notifyError(error)
            }
        },

        async addCategory() {
            try {
                const response = await axios.post('/api/v2/financial/expense-categories', this.newCategory)
                this.options.categories = response.data.content
                this.newCategory = { name: '', group: this.newCategory.group }
            } catch (error) {
                this.notifyError(error)
            }
        },

        async removeCategory(category) {
            try {
                const response = await axios.delete(`/api/v2/financial/expense-categories/${category.id}`)
                this.options.categories = response.data.content
            } catch (error) {
                this.notifyError(error)
            }
        },

        // ==== Exportação e formatação ====

        async exportList() {
            this.exporting = true
            try {
                const response = await axios.get('/api/v2/financial/expenses/export', {
                    params: { start: this.filter.start, end: this.filter.end, search: this.filter.search || '', category_id: this.filter.category_id || '', status: this.filter.status },
                    responseType: 'blob'
                })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(new Blob([response.data]))
                link.download = `despesas-${this.filter.start}-a-${this.filter.end}.xlsx`
                link.click()
                URL.revokeObjectURL(link.href)
            } catch (error) {
                this.$dialog.notify.error('Não foi possível exportar', { position: 'top-right', timeout: 4000 })
            } finally {
                this.exporting = false
            }
        },

        money(value) {
            return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },

        date(value) {
            return value ? moment(value).format('DD/MM/YYYY') : '-'
        },

        competence(value) {
            return value ? moment(value).format('MM/YYYY') : '-'
        }
    }
}
</script>

<style lang="scss" scoped>
.category-bar {
    display: grid;
    grid-template-columns: minmax(140px, 220px) 1fr 110px 50px;
    align-items: center;
    gap: 12px;
    padding: 4px 0;
    font-size: 13px;

    strong {
        text-align: right;
    }

    em {
        color: #8a96ab;
        font-style: normal;
        text-align: right;
    }
}

.category-bar__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.category-bar__track {
    height: 10px;
    border-radius: 5px;
    background: #eef1f6;
    overflow: hidden;

    div {
        height: 100%;
        border-radius: 5px;
    }
}
</style>
